import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { postResetPasswordMail, postForgotPassword } from "../erpClient";
import toast from "react-hot-toast";
import { useButtonTableStyle } from "../utils/useButtonTableStyle";

function ForgetPassword() {
  const { buttonClass } = useButtonTableStyle();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // console.log("ForgetPassword component rendered, path:", location.pathname);

  // Determine if we're on the reset password page and which format
  const isResetPageWithQuery = location.pathname === "/resetpassword";
  const isResetPageWithParam = location.pathname.startsWith("/resetpassword/");
  const isResetPage = isResetPageWithQuery || isResetPageWithParam;

  // Add redirect and email extraction logic
  useEffect(() => {
    // Handle direct reset page access
    if (isResetPageWithQuery) {
      const params = new URLSearchParams(location.search);
      const emailParam = params.get("email");
      const codeParam = params.get("code");

      // If user tries to access /resetpassword directly without email parameter, redirect to /forgotpassword
      if (!emailParam) {
        navigate("/forgetpassword");
        return;
      }

      try {
        // Decode the base64 email
        const decodedEmail = atob(emailParam);
        setEmail(decodedEmail);
      } catch (error) {
        console.error("Error decoding email:", error);
        // If there's an error decoding, redirect to forgot password
        navigate("/forgetpassword");
      }
    }
    // Handle encoded email in path parameter
    else if (isResetPageWithParam) {

      try {
        // Extract the encoded email from the URL path
        const encodedEmail = location.pathname.split("/")[2];

        if (!encodedEmail) {
          navigate("/forgetpassword");
          return;
        }


        // Decode the base64 email with padding and URL-safe character handling
        const padded = encodedEmail.padEnd(
          encodedEmail.length + ((4 - (encodedEmail.length % 4)) % 4),
          "="
        );
        const decodedEmail = atob(padded.replace(/-/g, "+").replace(/_/g, "/"));

        setEmail(decodedEmail);
      } catch (error) {
        console.error("Error decoding email:", error);
        // If there's an error decoding, redirect to forgot password
        navigate("/forgetpassword");
      }
    }
  }, [location, navigate]);

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    if (errors.email) setErrors({ ...errors, email: "" });
  };

  const handleNewPasswordChange = (e) => {
    setNewPassword(e.target.value);
    if (errors.newPassword) setErrors({ ...errors, newPassword: "" });
  };

  const handleConfirmPasswordChange = (e) => {
    setConfirmPassword(e.target.value);
    if (errors.confirmPassword) setErrors({ ...errors, confirmPassword: "" });
  };

  const validateEmailForm = () => {
    const newErrors = {};
    if (!email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = "Enter a valid email address (must include @)";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateResetForm = () => {
    const newErrors = {};

    // Validate password
    if (!newPassword) {
      newErrors.newPassword = "New password is required";
    } else if (newPassword.length < 6) {
      newErrors.newPassword = "Password must be at least 6 characters";
    }

    // Validate confirm password
    if (newPassword !== confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();

    if (!validateEmailForm()) return;

    setIsLoading(true);

    try {
      // Prepare data for API
      const requestData = {
        email: email,
      };


      const data = await postResetPasswordMail(requestData);

      if (data && data.message === "radha") {
        toast.success("Reset link sent to your email");
        navigate("/login", {
          state: {
            message: "Please check your email for password reset instructions",
          },
        });
      } else {
        toast.error(data?.message || "Failed to send reset link");
      }
    } catch (error) {
      console.error("Password reset request error:", error);
      toast.error(
        error.response?.data?.message ||
          "Failed to process request. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetSubmit = async (e) => {
    e.preventDefault();

    if (!validateResetForm()) return;

    setIsLoading(true);

    try {
      // Prepare data for API
      const resetData = {
        email: email,
        new_password: newPassword,
      };


      // Send reset request to the forgot_pass endpoint
      const data = await postForgotPassword(resetData);

      if (data && data.message === "radha") {
        toast.success("Password reset successful");
        // Redirect to login page
        navigate("/login");
      } else {
        toast.error(data.message || "Password reset failed");
      }
    } catch (error) {
      console.error("Password reset error:", error);
      toast.error(
        error.response?.data?.message ||
          "Failed to reset password. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      id="main-wrapper"
      style={{ overflowX: "hidden", backgroundColor: "#f8f9fa" }}
    >
      <div className="container-fluid">
        <div
          className="position-relative min-vh-100 d-flex align-items-center justify-content-center"
          style={{
            width: "100vw",
            margin: 0,
            padding: 0,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <div
            className="d-flex align-items-center justify-content-center w-100"
            style={{ margin: 0, padding: 0, width: "100%" }}
          >
            <div
              className="col-md-6 col-xl-4 d-flex justify-content-center align-items-center p-4"
              style={{ maxWidth: "100%", margin: 0, padding: 0 }}
            >
              <div
                className="card mb-0 bg-body auth-login w-100"
                style={{
                  maxWidth: "100%",
                  margin: 0,
                  padding: 0,
                  boxShadow: "0 0 20px rgba(0, 0, 0, 0.1)",
                }}
              >
                <div className="card-body">
                  <Link
                    to="/"
                    className="text-nowrap logo-img d-block mb-4 w-100"
                  >
                    <img
                      src="/assets/images/logos/logo_1.png"
                      className="dark-logo"
                      alt="Logo-Dark"
                    />
                  </Link>
                  {/* Content based on current page */}
                  {isResetPage ? (
                    <>
                      <h2 className="lh-base mb-4">Reset Your Password</h2>
                      <p className="text-muted mb-4">
                        Enter your new password to complete the reset process.
                      </p>
                      <form onSubmit={handleResetSubmit}>
                        <div className="mb-3">
                          <label htmlFor="text-email" className="form-label">
                            Email Address
                          </label>
                          <input
                            type="email"
                            className="form-control"
                            id="text-email"
                            value={email}
                            readOnly
                            disabled
                            aria-describedby="emailHelp"
                          />
                        </div>
                        <div className="mb-3">
                          <label htmlFor="new-password" className="form-label">
                            New Password
                          </label>
                          <input
                            type="password"
                            className={`form-control ${
                              errors.newPassword ? "is-invalid" : ""
                            }`}
                            id="new-password"
                            value={newPassword}
                            onChange={handleNewPasswordChange}
                            placeholder="Enter new password"
                          />
                          {errors.newPassword && (
                            <div className="invalid-feedback">
                              {errors.newPassword}
                            </div>
                          )}
                        </div>
                        <div className="mb-4">
                          <label
                            htmlFor="confirm-password"
                            className="form-label"
                          >
                            Confirm Password
                          </label>
                          <input
                            type="password"
                            className={`form-control ${
                              errors.confirmPassword ? "is-invalid" : ""
                            }`}
                            id="confirm-password"
                            value={confirmPassword}
                            onChange={handleConfirmPasswordChange}
                            placeholder="Confirm new password"
                          />
                          {errors.confirmPassword && (
                            <div className="invalid-feedback">
                              {errors.confirmPassword}
                            </div>
                          )}
                        </div>
                        <button
                          type="submit"
                          className={`btn btn-dark w-100 py-8 mb-4 rounded-1 ${buttonClass}`}
                          disabled={isLoading}
                        >
                          {isLoading
                            ? "Resetting Password..."
                            : "Reset Password"}
                        </button>
                      </form>
                    </>
                  ) : (
                    <>
                      <h3 className="mb-3">Forgot Password</h3>
                      <p className="text-muted mb-4">
                        Enter your email address and we'll send you a link to
                        reset your password.
                      </p>
                      <form onSubmit={handleEmailSubmit}>
                        <div className="mb-3">
                          <label htmlFor="text-email" className="form-label">
                            Email Address
                          </label>
                          <input
                            type="email"
                            className={`form-control ${
                              errors.email ? "is-invalid" : ""
                            }`}
                            id="text-email"
                            value={email}
                            onChange={handleEmailChange}
                            placeholder="Enter your email"
                            aria-describedby="emailHelp"
                          />
                          {errors.email && (
                            <div className="invalid-feedback">
                              {errors.email}
                            </div>
                          )}
                        </div>
                        <button
                          type="submit"
                          className={`btn btn-dark w-100 py-8 mb-4 rounded-1 ${buttonClass}`}
                          disabled={isLoading}
                        >
                          {isLoading
                            ? "Sending Reset Link..."
                            : "Send Reset Link"}
                        </button>
                      </form>
                    </>
                  )}

                  <Link
                    to="/Login"
                    className={`btn bg-primary-subtle text-primary w-100 py-8 mb-4 rounded-1 ${buttonClass}`}
                  >
                    Back to Login
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ForgetPassword;
